import ky from 'ky';
import { API_BASE, OAUTH2_CLIENT_ID } from '../lib/env';

// Token refresh handler - will be set by auth store
let refreshTokenHandler: (() => Promise<string>) | null = null;

export const setRefreshTokenHandler = (handler: () => Promise<string>) => {
  refreshTokenHandler = handler;
};

export const clearRefreshTokenHandler = () => {
  refreshTokenHandler = null;
};

// Public API client - for unauthenticated requests (health, setup check)
export const publicApi = ky.create({
  prefixUrl: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  retry: {
    limit: 0, // Disable automatic retries to prevent loops
  },
  hooks: {
    afterResponse: [
      async (_request, _options, response) => {
        // Handle 403 - forbidden
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({ message: 'Access denied' })) as { message?: string };
          throw new Error(errorData.message || 'Access denied');
        }

        // Handle 404 - not found
        if (response.status === 404) {
          throw new Error('Resource not found');
        }

        // Handle 409 - conflict
        if (response.status === 409) {
          const errorData = await response.json().catch(() => ({ message: 'Conflict' })) as { message?: string; error?: string };
          throw new Error(errorData.message || errorData.error || 'Conflict');
        }

        // Handle 500+ server errors
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }

        return response;
      },
    ],
  },
});

// OAuth2 client - for token exchange (no auth headers, no CSRF)
export const oauthApi = ky.create({
  prefixUrl: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  retry: {
    limit: 0, // Disable automatic retries to prevent loops
  },
  hooks: {
    afterResponse: [
      async (_request, _options, response) => {
        // Handle 400 - bad request (validation errors, etc.)
        if (response.status === 400) {
          const errorData = await response.json().catch(() => ({ message: 'Bad request' })) as { message?: string; error?: string };
          throw new Error(errorData.message || errorData.error || 'Bad request');
        }

        // Handle 401 - invalid credentials
        if (response.status === 401) {
          const errorData = await response.json().catch(() => ({ message: 'Invalid credentials' })) as { message?: string };
          throw new Error(errorData.message || 'Invalid credentials');
        }

        // Handle 403 - forbidden
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({ message: 'Access denied' })) as { message?: string };
          throw new Error(errorData.message || 'Access denied');
        }

        // Handle 409 - conflict
        if (response.status === 409) {
          const errorData: any = await response.json().catch(() => ({ message: 'Conflict' }));
          throw new Error(errorData.message || errorData.error || 'Conflict');
        }

        // Handle 500+ server errors
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }

        return response;
      },
    ],
  },
});

// OAuth2 authenticated client - for OAuth2 API calls that need auth but no credentials
// This is used for endpoints like /oauth2/userinfo that need Bearer tokens but cause CORS issues with credentials: 'include'
export const oauthAuthenticatedApi = ky.create({
  prefixUrl: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  retry: {
    limit: 0, // Disable automatic retries to prevent loops
  },
  hooks: {
    beforeRequest: [
      (request) => {
        // Add OAuth2 access token if available
        const accessToken = localStorage.getItem('oauth_access_token');
        if (accessToken) {
          request.headers.set('Authorization', `Bearer ${accessToken}`);
        }
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        // Handle 400 - bad request
        if (response.status === 400) {
          const errorData = await response.json().catch(() => ({ message: 'Bad request' })) as { message?: string; error?: string };
          throw new Error(errorData.message || errorData.error || 'Bad request');
        }

        // Handle 401 - invalid credentials (no refresh for oauthAuthenticatedApi)
        if (response.status === 401) {
          // Clear auth tokens and redirect to login
          localStorage.removeItem('oauth_access_token');
          localStorage.removeItem('oauth_refresh_token');
          localStorage.removeItem('oauth_token_expiry');
          localStorage.removeItem('auth-storage');
          window.location.href = '/login';
          const errorData = await response.json().catch(() => ({ message: 'Session expired' })) as { message?: string };
          throw new Error(errorData.message || 'Session expired. Please login again.');
        }

        // Handle 403 - forbidden
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({ message: 'Access denied' })) as { message?: string };
          throw new Error(errorData.message || 'Access denied');
        }

        // Handle 409 - conflict
        if (response.status === 409) {
          const errorData = await response.json().catch(() => ({ message: 'Conflict' })) as { message?: string; error?: string };
          throw new Error(errorData.message || errorData.error || 'Conflict');
        }

        // Handle 500+ server errors
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }

        return response;
      },
    ],
  },
});

// Main API client - for authenticated requests
export const api = ky.create({
  prefixUrl: API_BASE,
  credentials: 'include', // Include cookies in requests
  headers: {
    'Content-Type': 'application/json',
  },
  retry: {
    limit: 0, // Disable ky's automatic retry - we handle 401 manually in afterResponse
    // ky will still retry on network errors, but not on HTTP status codes
  },
  hooks: {
    beforeRequest: [
      (request) => {
        // Add OAuth2 access token if available
        const accessToken = localStorage.getItem('oauth_access_token');
        if (accessToken) {
          request.headers.set('Authorization', `Bearer ${accessToken}`);
        }

        // Add CSRF token if available (from localStorage for now)
        const csrfToken = localStorage.getItem('csrf_token');
        if (csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(request.method)) {
          request.headers.set('X-CSRF-Token', csrfToken);
        }
      },
    ],
    afterResponse: [
      async (request, _options, response) => {
        // Handle 401 - attempt token refresh
        if (response.status === 401 && refreshTokenHandler) {
          // Prevent refresh token calls from triggering their own refresh
          const isRefreshRequest = request.url.includes('/refresh') || request.url.includes('/token');
          if (isRefreshRequest) {
            // Don't try to refresh a refresh request - just fail
            localStorage.removeItem('oauth_access_token');
            localStorage.removeItem('oauth_refresh_token');
            localStorage.removeItem('oauth_token_expiry');
            localStorage.removeItem('auth-storage');
            window.location.href = '/login';
            throw new Error('Session expired. Please login again.');
          }

          try {
            // Attempt to refresh token
            await refreshTokenHandler();

            // Clone the request with new Authorization header
            const newRequest = new Request(request, {
              headers: new Headers(request.headers)
            });

            // Update the Authorization header with the new token
            const newAccessToken = localStorage.getItem('oauth_access_token');
            if (newAccessToken) {
              newRequest.headers.set('Authorization', `Bearer ${newAccessToken}`);
            }

            // Make the retry request directly and return its result
            // This avoids the ky retry mechanism and prevents infinite loops
            const retryResponse = await fetch(newRequest);

            // Process the retry response through our error handlers
            if (retryResponse.status === 401) {
              // Still getting 401 after refresh, redirect to login
              localStorage.removeItem('oauth_access_token');
              localStorage.removeItem('oauth_refresh_token');
              localStorage.removeItem('oauth_token_expiry');
              localStorage.removeItem('auth-storage');
              window.location.href = '/login';
              throw new Error('Session expired. Please login again.');
            }

            // Handle other error statuses for the retry
            if (retryResponse.status === 400) {
              const errorData = await retryResponse.json().catch(() => ({ message: 'Bad request' }));
              throw new Error(errorData.message || errorData.error || 'Bad request');
            }

            if (retryResponse.status === 403) {
              const errorData = await retryResponse.json().catch(() => ({ message: 'Access denied' }));
              throw new Error(errorData.message || 'Access denied');
            }

            if (retryResponse.status === 404) {
              throw new Error('Resource not found');
            }

            if (retryResponse.status === 409) {
              const errorData = await retryResponse.json().catch(() => ({ message: 'Conflict' }));
              throw new Error(errorData.message || errorData.error || 'Conflict');
            }

            if (retryResponse.status >= 500) {
              throw new Error('Server error. Please try again later.');
            }

            return retryResponse;
          } catch {
            // Refresh failed, clear auth and redirect to login
            localStorage.removeItem('oauth_access_token');
            localStorage.removeItem('oauth_refresh_token');
            localStorage.removeItem('oauth_token_expiry');
            localStorage.removeItem('auth-storage');
            window.location.href = '/login';
            throw new Error('Session expired. Please login again.');
          }
        } else if (response.status === 401) {
          // No refresh handler, clear auth and redirect to login
          localStorage.removeItem('oauth_access_token');
          localStorage.removeItem('oauth_refresh_token');
          localStorage.removeItem('oauth_token_expiry');
          localStorage.removeItem('auth-storage'); // Clear Zustand persisted state
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }

        // Handle 400 - bad request (validation errors, setup already done, etc.)
        if (response.status === 400) {
          const errorData: any = await response.json().catch(() => ({ message: 'Bad request' }));
          throw new Error(errorData.message || errorData.error || 'Bad request');
        }

        // Handle 403 - forbidden
        if (response.status === 403) {
          const errorData: any = await response.json().catch(() => ({ message: 'Access denied' }));
          throw new Error(errorData.message || 'Access denied');
        }

        // Handle 404 - not found
        if (response.status === 404) {
          throw new Error('Resource not found');
        }

        // Handle 409 - conflict (e.g., email already exists)
        if (response.status === 409) {
          const errorData: any = await response.json().catch(() => ({ message: 'Conflict' }));
          throw new Error(errorData.message || errorData.error || 'Conflict');
        }

        // Handle 500+ server errors
        if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        }

        return response;
      },
    ],
  },
});

// Health check
export const healthCheck = async () => {
  return api.get('health').json();
};

// Public health check (no auth required)
export const publicHealthCheck = async () => {
  return publicApi.get('health').json();
};

// Custom login endpoint (for custom login page - returns tokens directly)
export const customLogin = async (email: string, password: string) => {
  return publicApi.post('api/v1/users/login', {
    json: { client_id: OAUTH2_CLIENT_ID, email, password }
  }).json();
};

// Auth endpoints
export const login = async (email: string, password: string) => {
  return api.post('api/v1/users/login', { json: { client_id: OAUTH2_CLIENT_ID, email, password } }).json();
};

export const setupSuperAdmin = async (data: {
  secret: string;
  email: string;
  password: string;
  name?: string;
}) => {
  // Use publicApi client (no credentials: 'include') to avoid CORS issues
  // The setup endpoint is public and doesn't need cookies
  return publicApi.post('api/v1/admin/setup', { json: data }).json();
};

export const logout = async (refreshToken?: string) => {
  return api.post('api/v1/users/logout', {
    json: refreshToken ? { refresh_token: refreshToken } : undefined,
    credentials: 'include', // Explicitly ensure cookies are sent
  }).json();
};

export const refreshToken = async (refreshToken: string) => {
  return api.post('api/v1/users/refresh', {
    json: { refresh_token: refreshToken }
  }).json();
};

export const getProfile = async () => {
  return api.get('api/v1/users/profile').json();
};

export const updateProfile = async (data: { name?: string; email?: string }) => {
  return api.put('api/v1/users/profile', { json: data }).json();
};

export const changePassword = async (data: { current_password: string; new_password: string }) => {
  return api.put('api/v1/users/password', { json: data }).json();
};

export const requestPasswordReset = async (email: string) => {
  return publicApi.post('api/v1/users/forgot-password', {
    json: { email, client_id: OAUTH2_CLIENT_ID }
  }).json();
};

export const resetPassword = async (token: string, newPassword: string) => {
  return publicApi.post('api/v1/users/reset-password', {
    json: { token, new_password: newPassword }
  }).json();
};

export const activateUser = async (userId: string, data: { email_verified?: boolean; is_active?: boolean }) => {
  return api.put(`api/v1/users/${userId}/activate`, { json: data }).json();
};

// Organization endpoints
export const getOrganizations = async () => {
  return api.get('api/v1/organizations').json();
};

export const getOrganization = async (id: string) => {
  return api.get(`api/v1/organizations/${id}`).json();
};

export const createOrganization = async (data: { name: string; domain?: string }) => {
  return api.post('api/v1/organizations', { json: data }).json();
};

export const updateOrganization = async (id: string, data: { name?: string; domain?: string }) => {
  return api.put(`api/v1/organizations/${id}`, { json: data }).json();
};

// Application endpoints
export const getApplications = async (orgId?: string) => {
  if (orgId) {
    return api.get(`api/v1/organizations/${orgId}/apps`).json();
  }
  // Super admin - get all apps (would need a different endpoint)
  // For now, this requires orgId
  throw new Error('Organization ID required');
};

export const getApplication = async (orgId: string, appId: string) => {
  return api.get(`api/v1/organizations/${orgId}/apps/${appId}`).json();
};

export const createApplication = async (orgId: string, data: {
  name: string;
  redirectUris: string[];
  appUrl?: string;
  logoUrl?: string;
  logUrl?: string;
  scope?: string[];
}) => {
  return api.post(`api/v1/organizations/${orgId}/apps`, { json: data }).json();
};

export const updateApplication = async (orgId: string, appId: string, data: {
  name?: string;
  redirectUris?: string[];
  appUrl?: string;
  logoUrl?: string;
  logUrl?: string;
  scope?: string[];
  isActive?: boolean;
}) => {
  return api.put(`api/v1/organizations/${orgId}/apps/${appId}`, { json: data }).json();
};

// API Key endpoints
export const createApiKey = async (orgId: string, data: {
  name: string;
  permissions?: string[];
}) => {
  return api.post(`api/v1/organizations/${orgId}/api-keys`, { json: data }).json();
};

// User API Key endpoints
export const getUserApiKeys = async () => {
  return api.get('api/v1/users/me/api-keys').json();
};

export const createUserApiKey = async (data: { name: string; permissions?: string[] }) => {
  return api.post('api/v1/users/me/api-keys', { json: data }).json();
};

export const getUserApiKey = async (keyId: string) => {
  return api.get(`api/v1/users/me/api-keys/${keyId}`).json();
};

export const deleteUserApiKey = async (keyId: string) => {
  return api.delete(`api/v1/users/me/api-keys/${keyId}`).json();
};

// User Management endpoints (Super Admin only)
export const getUsers = async () => {
  return api.get('api/v1/admin/users').json();
};

export const getUser = async (userId: string) => {
  return api.get(`api/v1/users/${userId}`).json();
};

export const createUser = async (data: {
  email: string;
  password: string;
  name?: string;
  role: 'user' | 'admin';
  orgId?: string;
}) => {
  return api.post('api/v1/admin/users', { json: data }).json();
};

export const updateUser = async (userId: string, data: {
  name?: string;
  role?: 'user' | 'admin';
  email?: string;
}) => {
  return api.put(`api/v1/users/${userId}`, { json: data }).json();
};

export const lockUser = async (userId: string, durationMinutes?: number): Promise<any> => {
  const body: any = {};
  if (durationMinutes) {
    body.duration_minutes = durationMinutes;
  }
  return api.post(`api/v1/users/${userId}/lock`, { json: body }).json();
};

export const unlockUser = async (userId: string): Promise<any> => {
  return api.post(`api/v1/users/${userId}/unlock`).json();
};

export const getUserStats = async (userId: string): Promise<any> => {
  return api.get(`api/v1/users/${userId}/stats`).json();
};

export const sendVerificationEmail = async (email: string) => {
  return api.post('api/v1/users/send-verification', { json: { email } }).json();
};

// Social login endpoints (for reference)
export const socialLogin = async (data: {
  provider: 'google' | 'github';
  providerUserId: string;
  email: string;
  name?: string;
}) => {
  return api.post('api/v1/users/social', { json: { client_id: OAUTH2_CLIENT_ID, ...data } }).json();
};

// OAuth2 endpoints (for future use if needed)
export const oauthAuthorize = async (params: {
  client_id: string;
  redirect_uri: string;
  response_type: 'code';
  scope?: string;
  state?: string;
  code_challenge: string;
  code_challenge_method: 'S256';
}) => {
  const queryString = new URLSearchParams(params as any).toString();
  return api.get(`oauth2/authorize?${queryString}`).json();
};

export const oauthToken = async (data: {
  grant_type: 'authorization_code' | 'refresh_token';
  code?: string;
  redirect_uri?: string;
  code_verifier?: string;
  refresh_token?: string;
  client_id?: string;
  client_secret?: string;
}) => {
  // OAuth2 token endpoint typically uses form-encoded data
  const formData = new URLSearchParams();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined) {
      formData.append(key, value.toString());
    }
  });

  return oauthApi.post('oauth2/token', {
    body: formData,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  }).json();
};

export const oauthUserinfo = async () => {
  // Use oauthAuthenticatedApi client for userinfo to avoid credentials: 'include' CORS issues
  // This client adds the Bearer token but doesn't use credentials: 'include'
  return oauthAuthenticatedApi.get('oauth2/userinfo').json();
};

export const oauthRevoke = async (token: string, clientId?: string) => {
  return oauthApi.post('oauth2/revoke', {
    json: { token, client_id: clientId }
  }).json();
};

// OAuth2 logout URL builder (for RP-initiated logout)
// Implements OIDC end_session_endpoint spec
export const buildOAuth2LogoutUrl = (
  idTokenHint: string,
  postLogoutRedirectUri: string,
  clientId: string,
  state?: string
): string => {
  const params = new URLSearchParams({
    id_token_hint: idTokenHint,
    post_logout_redirect_uri: postLogoutRedirectUri,
    client_id: clientId, // Required for redirect URI validation
  });

  if (state) {
    params.append('state', state);
  }

  return `${API_BASE}/oauth2/logout?${params.toString()}`;
};

// Audit Log endpoints (Super Admin only)
export const getAuditLogs = async (filters?: {
  user_ids?: string;
  organization_ids?: string;
  client_ids?: string;
  event_types?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}) => {
  const params = new URLSearchParams();
  if (filters) {
    if (filters.user_ids) params.append("user_ids", filters.user_ids);
    if (filters.organization_ids) params.append("organization_ids", filters.organization_ids);
    if (filters.client_ids) params.append("client_ids", filters.client_ids);
    if (filters.event_types) params.append("event_types", filters.event_types);
    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());
  }

  const queryString = params.toString();
  return api.get(`api/v1/admin/audit${queryString ? `?${queryString}` : ""}`).json();
};
